// @flow
import React, { type Element } from 'react';
import classNames from 'classnames';
import { compose, pure } from 'recompose';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { analyticsActions, Track } from 'angieslist-app-platform';
import { formatPhoneUrl, formatPhoneNumber } from 'angieslist-app-platform/lib/common/utils/stringUtils';
import { Button } from 'angieslist-react-components';
import { CheckmarkIcon24, EmailIcon24, PhoneIcon24, FaqIcon24 } from '@angi/icons/design-system-icons/ui-icons/24';
import './helpCard.scss';
import * as advertiseActions from '../../../../app/common/advertise/advertiseActions';
import type { AdvertiseReducerState } from '../../../../app/common/advertise/advertiseReducer';
import { getServiceProviderUpgradeState, PAID_UPGRADE_STATE, FREE_UPGRADE_STATE } from '../../common/selectors/upgradeStateSelectors';
import { getAccountOwnerDetails } from '../../common/selectors/accountOwnerSelectors';
import type { AccountOwnerDetailsType } from '../../common/selectors/accountOwnerSelectors';
import type { UpgradeStateType } from '../../common/selectors/upgradeStateSelectors';

type Props = {
  bottomHeader: string;
  bottomCta: string;
  description: string;
  confirmationMsg: string;
  helpCenterLink: string;
  advertise: AdvertiseReducerState;
  serviceProviderProfile: any;
  advertiseRequest: (
    activityLocation: string,
    description: string,
    categorySegmentData: {
      categoryGroupId: number;
      contactCountDisplayed: number;
    }
  ) => void;
  activityLocation: string;
  accountOwnerDetails: AccountOwnerDetailsType;
  serviceProviderUpgradeState: UpgradeStateType,
}

const HelpCard = (props: Props): Element<"div"> => {
  const {
    bottomHeader,
    bottomCta,
    description,
    confirmationMsg,
    helpCenterLink,
    advertiseRequest,
    advertise: {
      advertiseRequest: {
        isLoading: advertiseIsLoading,
        response: advertiseResponse,
        error: advertiseError,
      },
      contactCountByTopCategory: {
        response: contactCountByTopCategoryResponse,
      },
    },
    activityLocation,
    serviceProviderProfile,
    serviceProviderUpgradeState,
    accountOwnerDetails: {
      name,
      email,
      phone,
      isDefault,
    },
  } = props;
  const genericHelpHeader = 'Questions? We’re here to help.';
  const genericHelpCta = 'Visit help center';
  const paidHelpHeader = isDefault ? 'Need help? Talk to a representative.' : `Need help? Talk to your personal rep, ${name}.`;

  const isAdvertiseRequestSuccess = !advertiseError && advertiseResponse;
  const unmaskedPrimaryPhoneNumber = get(serviceProviderProfile, 'currentProfile.response.primaryContactInfo.unmaskedPrimaryPhoneNumber.original', '');
  const categoryGroupId = get(contactCountByTopCategoryResponse, 'categoryGroupContacts.categoryGroupId', 0);
  const contactCount = get(contactCountByTopCategoryResponse, 'categoryGroupContacts.contactCount', 0);

  return (
    <div id="help-card-container">
      <div className="help-card">
        <Choose>
          <When condition={serviceProviderUpgradeState === PAID_UPGRADE_STATE}>
            <div id="help-card-title-container" className="National2-Bold">
              {paidHelpHeader}
            </div>
            <div className="help-card-buttons">
              <Track>
                <Button
                  bsSize="small"
                  id="help-card--phone-button"
                  className="help-card-button National2-Medium"
                  bsStyle="dark"
                  href={`tel:${formatPhoneUrl(phone)}`}
                >
                  <PhoneIcon24 className="hand-raiser--icon" />
                  <span>{formatPhoneNumber(phone)}</span>
                </Button>
              </Track>
              <Track>
                <Button
                  bsStyle="small"
                  id="help-card-email-button"
                  className="help-card-button National2-Medium"
                  bsSize="small"
                  href={`mailto:${email}`}
                >
                  <EmailIcon24 className={classNames('hand-raiser--icon', { 'hide-icon': email.length > 23 })} />
                  <span>{email}</span>
                </Button>
              </Track>
              <Track>
                <Button
                  id="help-link-faq-button"
                  className="help-card-button National2-Medium"
                  bsStyle="dark"
                  href={helpCenterLink}
                >
                  <FaqIcon24 className="hand-raiser--icon" />
                  {genericHelpCta}
                </Button>
              </Track>
            </div>
          </When>
          <Otherwise>
            <div id="help-card-title-container" className="National2-Bold">
              {genericHelpHeader}
            </div>
            <div className="help-card-buttons">
              <Track>
                <Button
                  id="help-link-button"
                  className="help-card-button National2-Medium"
                  bsStyle="dark"
                  href={helpCenterLink}
                >
                  {genericHelpCta}
                </Button>
              </Track>
            </div>
          </Otherwise>
        </Choose>
      </div>

      {serviceProviderUpgradeState === FREE_UPGRADE_STATE && (
        <div className="help-card--hand-raiser help-card">
          <div id="help-card-title-container" className="National2-Bold">
            {bottomHeader}
          </div>
          <div id="help-card-subtitle-container" className="National2-Regular">
            {description}
          </div>
          {isAdvertiseRequestSuccess ?
            (
              <div className="hand-raiser--confirmation National2-Medium">
                <CheckmarkIcon24 className="hand-raiser--icon" />
                {`${confirmationMsg} ${unmaskedPrimaryPhoneNumber}`}.
              </div>
            ) :
            (
              <div className="help-card-buttons">
                <Track>
                  <Button
                    id="talk-to-a-rep-button"
                    className="help-card-button National2-Medium"
                    bsStyle="dark"
                    onClick={() => advertiseRequest(activityLocation, 'Generic CTA', {
                      categoryGroupId,
                      contactCountDisplayed: contactCount,
                    })}
                    disabled={advertiseIsLoading}
                  >
                    {bottomCta}
                  </Button>
                </Track>
              </div>
            )
          }
        </div>
      )}
    </div>
  );
};

export default (compose(
  connect(state => ({
    advertise: state.advertise,
    serviceProviderProfile: state.serviceProviderProfile,
    serviceProviderUpgradeState: getServiceProviderUpgradeState(state),
    accountOwnerDetails: getAccountOwnerDetails(state),
  }), {
    ...advertiseActions,
    ...analyticsActions,
  }),
  pure,
)(HelpCard): any);



// WEBPACK FOOTER //
// ./src/modules/home/browser/components/helpCard.js